import React, { useState, useEffect } from 'react';
import api from "../../../services/api";
import { formatDateWithTime } from '../../../utils/dateFormat';
import { useTableState } from '../utils/useTableState';
import ImageModal from './ImageModal';

const VersionsRow = ({ row, isExpanded, colSpan, onClose }) => {
    const [versions, setVersions] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const { modal, setModal, actionHandlers } = useTableState();

    useEffect(() => {
        if (isExpanded) {
            const fetchVersions = async () => {
                try {
                    setLoading(true);
                    setError(null);
                    setVersions([]);

                    const result = await api({
                        url: "/alpha_cst_designer/versions",
                        method: "POST",
                        responseType: "json",
                        data: { creative_id: row.creative_id },
                    });

                    if (result.code === 200) {
                        const versions = result.data?.data.img_urls || [];
                        const sortedVersions = versions.sort((a, b) => new Date(a.date) - new Date(b.date));
                        setVersions(sortedVersions);
                    } else {
                        setError("Failed to fetch versions");
                    }
                } catch (err) {
                    console.error(err);
                    setError("An error occurred while fetching versions.");
                } finally {
                    setLoading(false);
                }
            };

            fetchVersions();
        }
    }, [isExpanded, row.creative_id]);

    const isImage = (url) => /\.(jpeg|jpg|gif|png|webp|svg)(\?.*)?$/i.test(url);
    const isVideo = (url) => /\.(mp4|webm|ogg|mov|avi)(\?.*)?$/i.test(url);
    const isPDF = (url) => /\.pdf(\?.*)?$/i.test(url);

    if (!isExpanded) return null;

    return (
        <tr className="bg-gray-100">
            <td colSpan={colSpan}>
                <div className="relative">
                    <button
                        onClick={onClose}
                        className="absolute top-2 right-2 p-1 rounded-full hover:bg-gray-200 transition-colors"
                        aria-label="Close versions"
                    >
                        <svg
                            xmlns="http://www.w3.org/2000/svg"
                            className="h-5 w-5 text-gray-600"
                            viewBox="0 0 20 20"
                            fill="currentColor"
                        >
                            <path
                                fillRule="evenodd"
                                d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                                clipRule="evenodd"
                            />
                        </svg>
                    </button>
                    <div className="flex flex-wrap gap-4 py-4 px-4 justify-start">
                        {loading && <p className="text-gray-600 w-full text-center">Loading versions...</p>}
                        {error && <p className="text-red-500 w-full text-center">{error}</p>}
                        {!loading && versions.length === 0 && !error && (
                            <p className="text-gray-400 w-full text-center">No versions available.</p>
                        )}
                        {!loading &&
                            versions.map((img, i) => (
                                <div key={i} className="flex flex-col items-start">
                                    <div className="flex flex-col items-start">
                                        {img.video_url ? (
                                            <video
                                                width="96"
                                                height="auto"
                                                controls
                                                poster={img.url}
                                                onClick={() => {
                                                    setModal({
                                                        open: true,
                                                        type: 'latestDesign',
                                                        image: img.video_url
                                                    });
                                                }}
                                                className="rounded-md border border-gray-300 cursor-pointer"
                                            >
                                                <source src={img.video_url} type="video/mp4" />
                                                Your browser does not support the video tag.
                                            </video>
                                        ) : isImage(img.url) ? (
                                            <img
                                                src={img.url}
                                                alt={`Version ${i + 1}`}
                                                onClick={() => {
                                                    setModal({
                                                        open: true,
                                                        type: 'latestDesign',
                                                        image: img.url
                                                    });
                                                }}
                                                className="h-24 w-24 object-contain rounded-md border border-gray-300 cursor-pointer hover:opacity-80 transition-opacity"
                                            />
                                        ) : isVideo(img.url) ? (
                                            <video
                                                width="96"
                                                height="auto"
                                                controls
                                                onClick={() => {
                                                    setModal({
                                                        open: true,
                                                        type: 'latestDesign',
                                                        image: img.url
                                                    });
                                                }}
                                                className="rounded-md border border-gray-300 cursor-pointer"
                                            >
                                                <source src={img.url} type="video/mp4" />
                                                Your browser does not support the video tag.
                                            </video>
                                        ) : isPDF(img.url) ? (
                                            <a
                                                href={img.url}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="h-24 w-24 flex flex-col items-center justify-center border border-red-200 rounded-md bg-red-50 hover:bg-red-100 transition-colors gap-1 cursor-pointer"
                                            >
                                                <svg className="w-8 h-8 text-red-500" fill="currentColor" viewBox="0 0 24 24">
                                                    <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8l-6-6zm-1 1.5L18.5 9H13V3.5zM6 20V4h5v7h7v9H6z"/>
                                                </svg>
                                                <span className="text-[9px] text-red-600 font-medium">PDF</span>
                                            </a>
                                        ) : (
                                            <div className="h-24 w-24 flex items-center justify-center border border-gray-300 rounded-md text-xs text-gray-500">Unsupported</div>
                                        )}
                                        <span className="font-bold text-xs mt-1">{i === 0 ? "Sample" : `Version ${i}`}</span>
                                        <span className="text-xs text-gray-500">{formatDateWithTime(img.date)}</span>
                                    </div>
                                </div>
                            ))}
                    </div>
                    <ImageModal
                        isOpen={modal.open && modal.type === 'latestDesign'}
                        image={modal.image}
                        alt="version preview"
                        onClose={actionHandlers.closeModal}
                    />
                </div>
            </td>
        </tr>
    );
};

export default VersionsRow;
